import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Mic, MicOff, Music2, Send, Smile, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSpotify } from '../../contexts/SpotifyContext';
import VoiceRecorder from '../VoiceRecorder';
import GifPicker from './GifPicker';
import MentionPicker from './MentionPicker';

const EMOJI_CATEGORIES = {
  smileys: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '💩', '🤡', '👻', '👽', '🤖'],
  gestures: ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪', '🦾', '👋', '🤚', '🖐️', '✋', '🖖', '👈', '👉', '👆', '👇', '☝️', '👊', '✊', '🤛', '🤜', '💅', '✍️', '🤳'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '✨', '⭐', '🌟', '💫', '🔥', '💯', '🎉', '🎊', '🎈'],
  objects: ['✅', '❌', '⚠️', '💬', '💭', '📢', '🔔', '📌', '📎', '📁', '📂', '📅', '📆', '⏰', '⌛', '📱', '💻', '⌨️', '🖥️', '📷', '📸', '🎥', '🎵', '🎶', '🎧', '💡', '🔑', '🔒', '🔓', '✉️', '📧', '📦', '📝', '✏️'],
};

const CATEGORY_LABELS = {
  smileys: 'Smileys',
  gestures: 'Gestures',
  hearts: 'Hearts',
  objects: 'Objects',
  gifs: 'GIFs',
};

function compressImage(file, maxWidth = 1920, quality = 0.8) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            resolve(file);
            return;
          }
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to compress image'));
    };
    img.src = url;
  });
}

const MessageInput = ({
  onSend,
  onTyping,
  onUploadImage,
  disabled = false,
  sending = false,
  conversationId = null,
  conversationType = 'direct',
  editingMessage = null,
  onCancelEdit,
}) => {
  const allowUserMentions = conversationType === 'group';
  const isEditing = !!editingMessage;
  const { connected: spotifyConnected, currentTrack } = useSpotify();
  const [text, setText] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionType, setMentionType] = useState(allowUserMentions ? 'user' : 'task');
  const [pendingMentions, setPendingMentions] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState('smileys');
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const fileRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const typingTimeout = useRef(null);
  const imagePreviewRef = useRef(null);

  useEffect(() => {
    setMentionType(allowUserMentions ? 'user' : 'task');
  }, [allowUserMentions, conversationId]);

  useEffect(() => {
    if (isEditing) setShowVoiceRecorder(false);
  }, [isEditing]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('[data-emoji-toggle]')
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  useEffect(() => {
    return () => {
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
    };
  }, []);

  const triggerTyping = () => {
    onTyping?.(true);
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => onTyping?.(false), 2000);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    triggerTyping();

    const cursor = e.target.selectionStart;
    const before = value.slice(0, cursor);
    const match = before.match(/(?:^|\s)@([a-zA-Z0-9_:]*)$/);
    if (match) {
      setShowMentions(true);
      setMentionQuery(match[1] || '');
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? text.length;
    const end = textarea?.selectionEnd ?? text.length;
    const next = `${text.slice(0, start)}${emoji}${text.slice(end)}`;
    setText(next);
    triggerTyping();
    setShowEmojiPicker(false);
    setTimeout(() => {
      if (!textarea) return;
      textarea.focus();
      const pos = start + emoji.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertMention = (item) => {
    const label =
      typeof item?.display === 'string'
        ? item.display
        : item?.display?.name ||
          item?.display?.title ||
          item?.subtitle ||
          item?.id ||
          'mention';
    const mentionText = `@${item.type}:${label}`;
    const textarea = textareaRef.current;
    const cursor = textarea?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const match = before.match(/(?:^|\s)@([a-zA-Z0-9_:]*)$/);
    let next = text;
    let nextCursor = cursor;
    if (match) {
      const start = before.lastIndexOf('@');
      next = `${text.slice(0, start)}${mentionText} ${after}`;
      nextCursor = start + mentionText.length + 1;
    } else {
      next = `${before}${mentionText} ${after}`;
      nextCursor = cursor + mentionText.length + 1;
    }
    setText(next);
    setPendingMentions((prev) => [
      ...prev,
      { type: item.type, id: item.id, display: label },
    ]);
    setShowMentions(false);
    setTimeout(() => {
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
      const url = URL.createObjectURL(compressed);
      imagePreviewRef.current = url;
      setImageFile(compressed);
      setImagePreview(url);
    } catch (_) {
      if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
      const url = URL.createObjectURL(file);
      imagePreviewRef.current = url;
      setImageFile(file);
      setImagePreview(url);
    }
  };

  const clearImage = () => {
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = null;
    }
    setImagePreview(null);
    setImageFile(null);
  };

  useEffect(() => {
    if (!editingMessage) return;
    setText(editingMessage.content || '');
    setPendingMentions(editingMessage.mentions || []);
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = null;
    }
    setImagePreview(null);
    setImageFile(null);
    setShowMentions(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [editingMessage?.id]);

  const handleSend = async () => {
    const content = text.trim();
    if (isEditing) {
      if (!content || disabled || sending) return;
      try {
        await onSend?.({
          content,
          mentions: pendingMentions,
          editingMessageId: editingMessage.id,
        });
        setText('');
        setPendingMentions([]);
        setShowEmojiPicker(false);
        onTyping?.(false);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to update message');
      }
      return;
    }

    if ((!content && !imageFile) || disabled || sending) return;

    const mentionsSnapshot = pendingMentions;
    const textSnapshot = content;
    const fileSnapshot = imageFile;

    // Clear composer immediately so send feels instant
    setText('');
    setPendingMentions([]);
    clearImage();
    setShowEmojiPicker(false);
    onTyping?.(false);

    try {
      let imageUrl = null;
      if (fileSnapshot && onUploadImage) {
        const uploaded = await onUploadImage(fileSnapshot);
        imageUrl = uploaded?.url;
        if (!imageUrl) {
          toast.error('Failed to upload image');
          setText(textSnapshot);
          return;
        }
      }

      await onSend?.({
        content: textSnapshot,
        type: imageUrl ? 'image' : 'text',
        image_url: imageUrl,
        mentions: mentionsSnapshot,
      });
    } catch (err) {
      // Restore text on failure so the user can retry
      setText(textSnapshot);
      setPendingMentions(mentionsSnapshot);
      toast.error(err?.response?.data?.detail || 'Failed to send message');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const sendGif = async (gif) => {
    if (!gif?.url || disabled || sending || isEditing) return;
    setShowEmojiPicker(false);
    onTyping?.(false);
    try {
      await onSend?.({
        content: '',
        type: 'image',
        image_url: gif.url,
        mentions: [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send GIF');
    }
  };

  const shareNowPlaying = async () => {
    if (!spotifyConnected || !currentTrack || disabled || sending || isEditing) return;
    const trackName = currentTrack.name || 'Unknown track';
    const artist = currentTrack.artist || '';
    try {
      await onSend?.({
        content: `🎵 ${trackName}${artist ? ` — ${artist}` : ''}`,
        type: 'spotify',
        mentions: [],
        metadata: {
          track_name: trackName,
          artist,
          album: currentTrack.album || '',
          album_art_url: currentTrack.albumArt || null,
          spotify_uri: currentTrack.uri || null,
          duration_ms: currentTrack.durationMs || null,
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to share track');
    }
  };

  const handleVoiceNoteSend = async (audioBlob) => {
    if (!audioBlob || disabled || sending || isEditing) return;
    try {
      const file = new File([audioBlob], 'voice_note.webm', {
        type: audioBlob.type || 'audio/webm',
      });
      const uploaded = await onUploadImage?.(file);
      const imageUrl = uploaded?.url;
      if (!imageUrl) {
        toast.error('Failed to upload voice note');
        throw new Error('upload failed');
      }
      await onSend?.({
        content: '',
        type: 'voice',
        image_url: imageUrl,
        mentions: [],
      });
      setShowVoiceRecorder(false);
      setShowEmojiPicker(false);
      onTyping?.(false);
      toast.success('Voice note sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send voice note');
      throw err;
    }
  };

  const handleVoiceRecorderCancel = () => {
    setShowVoiceRecorder(false);
  };

  return (
    <div className="relative border-t border-secondary-200 dark:border-secondary-700 p-3 bg-white dark:bg-secondary-900" data-testid="message-input">
      {isEditing && (
        <div
          className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-primary-50 px-3 py-2 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
          data-testid="editing-banner"
        >
          <span className="font-medium">Editing message</span>
          <button
            type="button"
            onClick={() => {
              setText('');
              setPendingMentions([]);
              onCancelEdit?.();
            }}
            className="font-medium underline-offset-2 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}

      {showMentions && (
        <MentionPicker
          query={mentionQuery}
          activeType={mentionType}
          onTypeChange={setMentionType}
          onSelect={insertMention}
          onClose={() => setShowMentions(false)}
          conversationId={conversationId}
          allowUserMentions={allowUserMentions}
        />
      )}

      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="absolute bottom-full left-3 right-3 mb-2 z-50 rounded-xl border border-secondary-200 bg-white shadow-lg dark:border-secondary-600 dark:bg-secondary-800 overflow-hidden"
          data-testid="emoji-picker"
        >
          <div className="flex gap-1 px-2 pt-2 border-b border-secondary-100 dark:border-secondary-700 overflow-x-auto">
            {[...Object.keys(EMOJI_CATEGORIES), 'gifs'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setEmojiCategory(key)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-t-md whitespace-nowrap ${
                  emojiCategory === key
                    ? 'text-primary-600 border-b-2 border-primary-600 dark:text-primary-400'
                    : 'text-secondary-500 hover:text-secondary-700 dark:text-secondary-400'
                }`}
              >
                {CATEGORY_LABELS[key]}
              </button>
            ))}
          </div>
          {emojiCategory === 'gifs' ? (
            <GifPicker onSelect={sendGif} disabled={disabled || sending || isEditing} />
          ) : (
            <div className="grid grid-cols-8 gap-0.5 p-2 max-h-44 overflow-y-auto">
              {EMOJI_CATEGORIES[emojiCategory].map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="h-9 w-full text-lg rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700"
                  aria-label={`Insert ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {imagePreview && (
        <div className="mb-2 relative inline-block">
          <img
            src={imagePreview}
            alt="Preview"
            className="h-20 rounded-lg object-cover border border-secondary-200 dark:border-secondary-600"
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-secondary-800 text-white flex items-center justify-center"
            aria-label="Remove image"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2">
        {!isEditing && !showVoiceRecorder && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800"
            aria-label="Attach image"
            disabled={disabled}
          >
            <ImageIcon className="h-5 w-5" />
          </button>
        )}
        {!isEditing && !showVoiceRecorder && spotifyConnected && currentTrack && (
          <button
            type="button"
            onClick={shareNowPlaying}
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg text-[#1DB954] hover:bg-[#1DB954]/10"
            aria-label="Share now playing"
            title={`Share ${currentTrack.name}`}
            disabled={disabled || sending}
            data-testid="chat-share-spotify"
          >
            <Music2 className="h-5 w-5" />
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {showVoiceRecorder && !isEditing ? (
          <div className="flex-1 min-w-0" data-testid="chat-voice-recorder">
            <VoiceRecorder
              onSend={handleVoiceNoteSend}
              onCancel={handleVoiceRecorderCancel}
              disabled={disabled || sending}
              autoStart
            />
          </div>
        ) : (
          <div className="relative flex-1 min-w-0 self-center">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={
                isEditing
                  ? 'Edit your message…'
                  : allowUserMentions
                    ? 'Type a message… Use @ to mention'
                    : 'Type a message… Use @ for tasks or projects'
              }
              disabled={disabled || sending}
              className="w-full resize-none max-h-28 min-h-11 leading-5 pl-3 pr-[5.25rem] py-3 rounded-xl border border-secondary-200 bg-secondary-50 text-sm text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-100"
            />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setShowEmojiPicker(false);
                    setShowVoiceRecorder(true);
                  }}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-full text-secondary-500 hover:bg-secondary-200/70 dark:text-secondary-400 dark:hover:bg-secondary-700"
                  aria-label="Record voice note"
                  data-testid="chat-mic-button"
                  disabled={disabled || sending}
                  title="Record voice note"
                >
                  <Mic className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                data-emoji-toggle
                onClick={() => setShowEmojiPicker((open) => !open)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-full text-secondary-500 hover:bg-secondary-200/70 dark:text-secondary-400 dark:hover:bg-secondary-700"
                aria-label="Insert emoji"
                aria-expanded={showEmojiPicker}
                disabled={disabled || sending}
              >
                <Smile className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        {!showVoiceRecorder && (
          <button
            type="button"
            onClick={handleSend}
            disabled={
              disabled ||
              sending ||
              (isEditing ? !text.trim() : !text.trim() && !imageFile)
            }
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={isEditing ? 'Save message' : 'Send message'}
          >
            <Send className="h-5 w-5" />
          </button>
        )}
        {showVoiceRecorder && (
          <button
            type="button"
            onClick={handleVoiceRecorderCancel}
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            aria-label="Cancel voice recording"
            title="Cancel recording"
          >
            <MicOff className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
